// FLAGS: none
// The conversion event for a public event page: the account was created.
//
// Fired when registration succeeds, **not** when the account's email is verified. Those are different
// moments and often different browser sessions — `RegisterView` sends the visitor to a verify-email
// step and they are only authenticated later, from the emailed link, which they may open on another
// device the next day. Waiting for that would drop the attribution of everyone who does, which is a
// large and self-selecting share of sign-ups. Whether they go on to verify is a separate question and
// is not measured here.
//
// Reported at most once per browser session, because two callers can both be reached by one sign-up —
// see `hasAlreadyReported` below.
import { posthogService } from "@src/services/posthog.service";
import { AttributionService } from "@src/services/attribution.service";

/** Marks that this browser session has already reported an account creation. */
const REPORTED_IDENTIFIER = "accountCreatedReported";

/**
 * Whether this sign-up has already been counted.
 *
 * There are two callers and an SSO sign-up reaches **both**: `auth.service.ts` reports straight after
 * the external POST (before the `twoFactorVerification` branch, which returns early), and
 * `queries/registration.ts` reports when the wizard finishes. `useRegisterSetup` skips only the email
 * and password steps for an SSO return, so the rest of the wizard still runs and still ends at
 * `useRegisterUser`. Neither caller is wrong on its own — dropping either loses a real path — so the
 * "one account, one event" rule is enforced here instead (OI-25447).
 *
 * Session storage rather than a module flag: the two fires can straddle a full page load on the SSO
 * paths, which a module-level boolean would not survive. Same lifetime as the attribution it carries.
 *
 * Fails **open** — an unreadable store reports rather than stays silent. A duplicate skews one
 * customer's number; a swallowed event loses the activation count entirely, which is worse.
 */
const hasAlreadyReported = (): boolean => {
  try {
    return sessionStorage.getItem(REPORTED_IDENTIFIER) !== null;
  } catch {
    return false;
  }
};

const markReported = (): void => {
  try {
    sessionStorage.setItem(REPORTED_IDENTIFIER, "true");
  } catch {
    // Storage unavailable: nothing to remember with, so the guard above stays open. Not worth an
    // exception on a reporting path that must never break registration.
  }
};

/**
 * Carries the public-event attribution when the visitor arrived from one, and nothing when they did
 * not — an organic sign-up is the same event without those properties, so one funnel counts both.
 *
 * @example
 * trackAccountCreated();
 */
export function trackAccountCreated() {
  if (hasAlreadyReported()) {
    return;
  }
  markReported();

  const attribution = AttributionService.getFromSessionStorage();

  // Spread directly: `attribution` is null for an organic sign-up and spreading null is a no-op,
  // so the `|| {}` this used to carry was an empty object literal for nothing (SonarQube S7744).
  posthogService.capture("Account Created", { ...attribution });
}
